// src/pages/Users/Inscription/ValidateTrialModal.jsx
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../components/Button/Button";
import { FaCheck, FaSave, FaTimes, FaUserMinus } from "react-icons/fa";
import {
  removeAdherentFromGroup,
  updateAdherent, // ← on passe par cette route en priorité
  updateAdherentStatus, // ← fallback seulement
  getAdherentsByUser, // ← pour relire l’état réel après écriture
} from "../../../services/adherentServices";
import { sendNotificationToAdmin } from "../../../services/notificationServices";
import { getGroupName, getGroupsList } from "../../../services/groupServices";

const NON_VALID_STATUS = "Non Licencié - Arrêt";
const NON_VALID_STATUS_ASCII = "Non Licencie - Arret"; // fallback sans accents

const ValidateTrialModal = ({
  isOpen,
  onClose,
  adherent,
  user,
  currentGroupIds,
}) => {
  const [decision, setDecision] = useState("valide"); // "valide" | "non"
  const [isSaving, setIsSaving] = useState(false);

  const [resolvedGroupIds, setResolvedGroupIds] = useState([]);
  const [resolvedGroupNames, setResolvedGroupNames] = useState([]);

  useEffect(() => {
    if (isOpen) setDecision("valide"); // reset à chaque ouverture
  }, [isOpen]);

  // Résolution des groupes (IDs & noms)
  useEffect(() => {
    if (!isOpen) return;

    const resolveGroups = async () => {
      try {
        // 0) IDs fournis par le parent
        if (Array.isArray(currentGroupIds) && currentGroupIds.length > 0) {
          const ids = [...new Set(currentGroupIds.filter(Boolean))];
          setResolvedGroupIds(ids);

          const all = (await getGroupsList()) || [];
          const names = ids
            .map((id) => all.find((g) => g.id === id)?.nom || null)
            .filter(Boolean);
          setResolvedGroupNames([...new Set(names)]);
          return;
        }

        // 1) IDs présents sur l'adhérent
        const adhIds = new Set();
        if (Array.isArray(adherent?.group_ids))
          adherent.group_ids.forEach((id) => id && adhIds.add(id));
        if (adherent?.group_id) adhIds.add(adherent.group_id);
        if (adherent?.groupe_id) adhIds.add(adherent.groupe_id);

        if (adhIds.size > 0) {
          const ids = [...adhIds];
          setResolvedGroupIds(ids);

          const all = (await getGroupsList()) || [];
          const names = ids
            .map((id) => all.find((g) => g.id === id)?.nom || null)
            .filter(Boolean);
          setResolvedGroupNames([...new Set(names)]);
          return;
        }

        // 2) Fallback: noms → IDs
        if (adherent?.id) {
          const namesFromApi = (await getGroupName(adherent.id)) || [];
          setResolvedGroupNames(
            Array.isArray(namesFromApi) ? namesFromApi : []
          );

          if (Array.isArray(namesFromApi) && namesFromApi.length > 0) {
            const all = (await getGroupsList()) || [];
            const ids = namesFromApi
              .map((n) => all.find((g) => (g.nom || g.name) === n)?.id || null)
              .filter(Boolean);
            setResolvedGroupIds([...new Set(ids)]);
            return;
          }
        }

        setResolvedGroupIds([]);
        setResolvedGroupNames([]);
      } catch (err) {
        console.error("[ValidateTrialModal] resolveGroups error:", err);
        setResolvedGroupIds([]);
        setResolvedGroupNames([]);
      }
    };

    resolveGroups();
  }, [isOpen, adherent?.id, currentGroupIds]);

  if (!isOpen) return null;

  // ---- Helpers robustes ----
  const readCurrentStatus = async () => {
    try {
      const list = await getAdherentsByUser(user?.userId);
      const found = Array.isArray(list)
        ? list.find((a) => a.id === adherent.id)
        : null;
      const status = found?.statut_inscription ?? null;
      console.log("[ValidateTrialModal] current DB status =", status);
      return status;
    } catch (e) {
      console.warn("[ValidateTrialModal] readCurrentStatus error", e);
      return null;
    }
  };

  const trySetStatusDirect = async (value) => {
    // On n’envoie QUE le champ attendu (pas de groupes) pour éviter tout effet de bord.
    try {
      const res = await updateAdherent(adherent.id, {
        statut_inscription: value,
      });
      console.log(
        "[ValidateTrialModal] updateAdherent(statut_inscription) =>",
        res
      );
      return true;
    } catch (e) {
      console.warn("[ValidateTrialModal] updateAdherent failed", e);
      return false;
    }
  };

  const trySetStatusFallbacks = async (value) => {
    // 1) Variation ASCII (si le backend filtre les accents)
    try {
      const res = await updateAdherent(adherent.id, {
        statut_inscription: NON_VALID_STATUS_ASCII,
      });
      console.log("[ValidateTrialModal] updateAdherent(ASCII) =>", res);
    } catch (e) {
      console.warn("[ValidateTrialModal] updateAdherent(ASCII) failed", e);
    }

    // 2) Endpoint alternatif (si ton routeur en a un dédié)
    try {
      const res2 = await updateAdherentStatus(adherent.id, value);
      console.log("[ValidateTrialModal] updateAdherentStatus =>", res2);
    } catch (e2) {
      console.warn("[ValidateTrialModal] updateAdherentStatus failed", e2);
    }
  };

  const ensureStatusIs = async (wanted) => {
    // 1) première écriture directe
    await trySetStatusDirect(wanted);

    // 2) lecture pour vérifier
    let current = await readCurrentStatus();
    if (current === wanted || current === NON_VALID_STATUS_ASCII) return true;

    // 3) fallbacks
    await trySetStatusFallbacks(wanted);

    // 4) relire
    current = await readCurrentStatus();
    return current === wanted || current === NON_VALID_STATUS_ASCII;
  };

  // ---------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!adherent?.id) {
      toast.error("Adhérent introuvable.");
      return;
    }

    const safeDecision =
      decision === "valide" || decision === "non" ? decision : "non";
    console.log("[ValidateTrialModal] submit decision:", safeDecision, {
      resolvedGroupIds,
      resolvedGroupNames,
    });

    try {
      setIsSaving(true);

      if (safeDecision === "valide") {
        // ✅ Valide → Inscrit (écriture simple via updateAdherent)
        const ok = await ensureStatusIs("Inscrit");
        if (!ok) {
          toast.error("Impossible de mettre à jour le statut en 'Inscrit'.");
          onClose(false);
          return;
        }

        const groupListStr = resolvedGroupNames?.length
          ? resolvedGroupNames.join(", ")
          : "—";
        const msg = `${user.prenom} ${user.nom} a validé le cours d’essai de ${adherent.prenom} ${adherent.nom} sur le groupe ${groupListStr}.`;
        await sendNotificationToAdmin(msg, user.token);

        toast.success("Cours d’essai validé. Statut mis à jour : Inscrit.");
      } else {
        // ❌ Non validé → retirer des groupes PUIS forcer statut Non Licencié - Arrêt (avec vérification)
        if (resolvedGroupIds?.length) {
          for (const gid of resolvedGroupIds) {
            try {
              await removeAdherentFromGroup(adherent.id, gid);
            } catch (err) {
              console.warn("Suppression du groupe échouée pour gid=", gid, err);
            }
          }
          toast.success("Place libérée dans le(s) groupe(s).");
        } else {
          toast.info("Aucun groupe détecté.");
        }

        // Forcer le statut (vérifié)
        const ok = await ensureStatusIs(NON_VALID_STATUS);
        if (!ok) {
          const still = await readCurrentStatus();
          toast.error(
            `Le backend refuse le statut. Statut actuel: ${still ?? "inconnu"}.`
          );
          onClose(false);
          return;
        }

        const groupListStr = resolvedGroupNames?.length
          ? resolvedGroupNames.join(", ")
          : "—";
        const msg = `${user.prenom} ${user.nom} n'a pas validé le cours d'essai de ${adherent.prenom} ${adherent.nom} sur le groupe ${groupListStr}. Place libérée.`;
        await sendNotificationToAdmin(msg, user.token);

        toast.success(`Statut mis à jour : ${NON_VALID_STATUS}.`);
      }

      onClose(true);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la mise à jour.");
      onClose(false);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm grid place-items-center px-3"
      onClick={() => onClose(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="trial-title"
    >
      <div
        className="w-full max-w-xl rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/70"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
          <h3
            id="trial-title"
            className="px-6 py-4 text-white text-base sm:text-lg font-semibold text-center"
          >
            Validation du cours d’essai ?
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5">
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
            Choisissez l’issue du cours d’essai pour{" "}
            <span className="font-semibold">
              {(adherent?.nom || "").toUpperCase()} {adherent?.prenom || ""}
            </span>
            .
            <br />
            Validé : Inscription définitive sur le groupe: {resolvedGroupNames.join(", ") || "—"}
            <br />
            Non validé : Ne poursuit pas, place libérée dans le groupe.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label
              className={`flex items-start gap-3 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 p-4 cursor-pointer ${
                decision === "valide"
                  ? "bg-green-50/70 dark:bg-green-900/20"
                  : "bg-white/60 dark:bg-slate-900/40"
              }`}
            >
              <input
                type="radio"
                name="decision"
                value="valide"
                checked={decision === "valide"}
                onChange={() => setDecision("valide")}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <FaCheck />
                  Cours d’essai validé
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  Le statut passera à <b>Inscrit</b>.
                </div>
              </div>
            </label>

            <label
              className={`flex items-start gap-3 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 p-4 cursor-pointer ${
                decision === "non"
                  ? "bg-rose-50/70 dark:bg-rose-900/20"
                  : "bg-white/60 dark:bg-slate-900/40"
              }`}
            >
              <input
                type="radio"
                name="decision"
                value="non"
                checked={decision === "non"}
                onChange={() => setDecision("non")}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  <FaUserMinus />
                  Cours d’essai non validé
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  Libère la place dans le groupe
                </div>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="mt-5 flex justify-center gap-2">
            <Button
              text={isSaving ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              disabled={isSaving}
              className="!rounded-xl gap-2 disabled:opacity-60"
            />
            <Button
              text="Annuler"
              icon={FaTimes}
              type="button"
              onClick={() => onClose(false)}
              className="!rounded-xl gap-2"
            />
          </div>

        </form>
      </div>
    </div>
  );
};

export default ValidateTrialModal;
